import React, { useState, useEffect, useCallback } from 'react';

// ============================================================================
// PlatformAdminPage — Master admin dashboard
// Completely separate from store-admin auth. Uses fetch() directly against
// /api/v1/platform/*. Token persisted in localStorage under `platform_token`.
// ============================================================================

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:4000') + '/api/v1/platform';

const formatWon = (amount) => '₩' + Number(amount || 0).toLocaleString('ko-KR');
const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch { return iso; }
};

// ---------- API helper (uses platform_token) ----------
async function pfetch(path, { method = 'GET', body } = {}) {
  const token = localStorage.getItem('platform_token');
  const res = await fetch(API_BASE + path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) {
      localStorage.removeItem('platform_token');
      localStorage.removeItem('platform_admin');
    }
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return data;
}

// ---------- Toast ----------
let toastCounter = 0;
const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = 'info') => {
    const id = ++toastCounter;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3000);
  }, []);
  return { toasts, addToast };
};

// ---------- Login ----------
const LoginView = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await pfetch('/login', { method: 'POST', body: { email, password } });
      localStorage.setItem('platform_token', data.token);
      localStorage.setItem('platform_admin', JSON.stringify(data.admin));
      onLogin(data.admin);
    } catch (err) {
      setError(err.message || '로그인 실패');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg)',
      padding: 20,
    }}>
      <form
        onSubmit={handleSubmit}
        style={{
          background: '#fff',
          borderRadius: 20,
          padding: 36,
          maxWidth: 420,
          width: '100%',
          boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56, height: 56,
            borderRadius: 16,
            background: 'var(--primary-light)',
            color: 'var(--primary)',
            marginBottom: 14,
            fontWeight: 800,
            fontSize: 20,
          }}>
            PA
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>플랫폼 관리자</h1>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 13, marginTop: 6 }}>Master Admin Console</p>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>이메일</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={inputStyle}
            autoComplete="email"
          />
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>비밀번호</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            style={inputStyle}
            autoComplete="current-password"
          />
        </div>

        {error && (
          <div style={{ padding: 12, background: 'var(--danger-light)', color: 'var(--danger)', borderRadius: 10, fontSize: 13, marginBottom: 14 }}>
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: 14,
            background: 'var(--primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            fontWeight: 700,
            fontSize: 15,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.5 : 1,
          }}
        >
          {loading ? '로그인 중...' : '로그인'}
        </button>
      </form>
    </div>
  );
};

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8 };
const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  border: '1px solid var(--divider)',
  borderRadius: 12,
  fontSize: 15,
  outline: 'none',
  background: '#fff',
};

// ---------- Dashboard stats cards ----------
const StatCard = ({ label, value, subtitle, color }) => (
  <div style={{
    background: '#fff',
    borderRadius: 16,
    padding: 20,
    boxShadow: 'var(--shadow-card)',
  }}>
    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8, fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 800, color: color || 'var(--text)' }}>{value}</div>
    {subtitle && <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>{subtitle}</div>}
  </div>
);

const KYC_STATUS_META = {
  pending:   { label: '작성중',  color: 'var(--text-tertiary)', bg: '#F2F4F6' },
  submitted: { label: '검토중',  color: 'var(--warning)',       bg: 'var(--warning-light)' },
  approved:  { label: '승인',    color: 'var(--success)',       bg: 'var(--success-light)' },
  rejected:  { label: '반려',    color: 'var(--danger)',        bg: 'var(--danger-light)' },
};

const SETTLEMENT_STATUS_META = {
  pending:      { label: '대기',    color: 'var(--warning)', bg: 'var(--warning-light)' },
  approved:     { label: '승인',    color: 'var(--info)',    bg: 'var(--info-light)' },
  transferring: { label: '송금중',  color: 'var(--info)',    bg: 'var(--info-light)' },
  completed:    { label: '송금완료', color: 'var(--success)', bg: 'var(--success-light)' },
  failed:       { label: '실패',    color: 'var(--danger)',  bg: 'var(--danger-light)' },
};

const Pill = ({ status, meta }) => {
  const m = (meta || KYC_STATUS_META)[status] || { label: status, color: 'var(--text-secondary)', bg: '#F2F4F6' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      color: m.color,
      background: m.bg,
    }}>
      {m.label}
    </span>
  );
};

// ---------- Stores tab ----------
const StoresTab = ({ addToast }) => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [kycFilter, setKycFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (kycFilter !== 'all') params.set('kyc', kycFilter);
      const data = await pfetch('/stores' + (params.toString() ? `?${params}` : ''));
      setStores(data.stores || []);
    } catch (e) {
      addToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [q, kycFilter, addToast]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    if (!window.confirm('KYC를 승인하시겠습니까?')) return;
    try {
      await pfetch(`/stores/${id}/kyc-approve`, { method: 'PUT' });
      addToast('승인 완료', 'success');
      await load();
    } catch (e) { addToast(e.message, 'error'); }
  };

  const reject = async (id) => {
    const reason = window.prompt('반려 사유를 입력하세요');
    if (!reason) return;
    try {
      await pfetch(`/stores/${id}/kyc-reject`, { method: 'PUT', body: { reason } });
      addToast('반려 처리되었습니다', 'success');
      await load();
    } catch (e) { addToast(e.message, 'error'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="매장명 / 이메일 검색"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ ...inputStyle, flex: '1 1 260px' }}
        />
        <select value={kycFilter} onChange={(e) => setKycFilter(e.target.value)} style={{ ...inputStyle, flex: '0 0 180px' }}>
          <option value="all">전체 상태</option>
          <option value="pending">작성중</option>
          <option value="submitted">검토중</option>
          <option value="approved">승인</option>
          <option value="rejected">반려</option>
        </select>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: 'var(--bg)' }}>
                <th style={th}>매장</th>
                <th style={th}>사업자</th>
                <th style={th}>계좌</th>
                <th style={thR}>매출</th>
                <th style={thR}>주문</th>
                <th style={th}>KYC</th>
                <th style={th}>작업</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={empty}>불러오는 중...</td></tr>}
              {!loading && stores.length === 0 && <tr><td colSpan={7} style={empty}>매장이 없습니다</td></tr>}
              {!loading && stores.map((s) => (
                <tr key={s.id}>
                  <td style={td}>
                    <div style={{ fontWeight: 700 }}>{s.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{s.owner_email}</div>
                  </td>
                  <td style={td}>
                    <div>{s.business_name || '-'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                      {s.business_registration_number || '미등록'}
                    </div>
                  </td>
                  <td style={td}>
                    <div>{s.bank_name || '-'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{s.bank_account_number || ''}</div>
                  </td>
                  <td style={tdR}>{formatWon(s.total_revenue)}</td>
                  <td style={tdR}>{s.total_orders || 0}</td>
                  <td style={td}><Pill status={s.kyc_status} /></td>
                  <td style={td}>
                    {s.kyc_status === 'submitted' && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => approve(s.id)} style={btnPrimary}>승인</button>
                        <button onClick={() => reject(s.id)} style={btnDanger}>반려</button>
                      </div>
                    )}
                    {s.kyc_status === 'approved' && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>승인 완료</span>}
                    {s.kyc_status === 'rejected' && (
                      <button onClick={() => approve(s.id)} style={btnGhost}>재승인</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ---------- Settlements tab ----------
const SettlementsTab = ({ addToast }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await pfetch(`/settlements?status=${status}`);
      setItems(data.settlements || []);
    } catch (e) { addToast(e.message, 'error'); }
    finally { setLoading(false); }
  }, [status, addToast]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    if (!window.confirm('이 정산을 승인하시겠습니까?')) return;
    try {
      await pfetch(`/settlements/${id}/approve`, { method: 'PUT' });
      addToast('승인 완료', 'success');
      await load();
    } catch (e) { addToast(e.message, 'error'); }
  };

  const markTransferred = async (id) => {
    const ref = window.prompt('송금 참조번호를 입력하세요 (선택)') || '';
    try {
      await pfetch(`/settlements/${id}/mark-transferred`, {
        method: 'PUT',
        body: { transfer_reference: ref },
      });
      addToast('송금 완료 처리되었습니다', 'success');
      await load();
    } catch (e) { addToast(e.message, 'error'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['pending', 'approved', 'completed', 'failed', 'all'].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            style={{
              padding: '9px 16px',
              borderRadius: 999,
              border: 'none',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              background: status === s ? 'var(--primary)' : '#fff',
              color: status === s ? '#fff' : 'var(--text-secondary)',
              boxShadow: 'var(--shadow-card)',
            }}
          >
            {SETTLEMENT_STATUS_META[s]?.label || '전체'}
          </button>
        ))}
      </div>

      <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ background: 'var(--bg)' }}>
                <th style={th}>매장</th>
                <th style={th}>기간</th>
                <th style={thR}>매출</th>
                <th style={thR}>수수료</th>
                <th style={thR}>송금액</th>
                <th style={th}>계좌</th>
                <th style={th}>상태</th>
                <th style={th}>작업</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} style={empty}>불러오는 중...</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={8} style={empty}>정산 내역이 없습니다</td></tr>}
              {!loading && items.map((s) => (
                <tr key={s.id}>
                  <td style={td}>
                    <div style={{ fontWeight: 700 }}>{s.store_name || s.store_id}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{s.owner_email}</div>
                  </td>
                  <td style={td}>
                    <div style={{ fontSize: 12 }}>
                      {formatDate(s.period_start).slice(0, 10)} ~ {formatDate(s.period_end).slice(0, 10)}
                    </div>
                  </td>
                  <td style={tdR}>{formatWon(s.gross_amount)}</td>
                  <td style={{ ...tdR, color: 'var(--danger)' }}>
                    {formatWon((s.pg_fee || 0) + (s.platform_fee || 0))}
                  </td>
                  <td style={{ ...tdR, color: 'var(--primary)', fontWeight: 800 }}>{formatWon(s.net_amount)}</td>
                  <td style={td}>
                    <div style={{ fontSize: 12 }}>{s.bank_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{s.bank_account_number}</div>
                  </td>
                  <td style={td}><Pill status={s.status} meta={SETTLEMENT_STATUS_META} /></td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {s.status === 'pending' && (
                        <button onClick={() => approve(s.id)} style={btnPrimary}>승인</button>
                      )}
                      {(s.status === 'pending' || s.status === 'approved') && (
                        <button onClick={() => markTransferred(s.id)} style={btnGhost}>송금완료</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ---------- Audit logs tab ----------
const AuditTab = ({ addToast }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await pfetch('/audit-logs?limit=100');
        setLogs(data.logs || []);
      } catch (e) { addToast(e.message, 'error'); }
      finally { setLoading(false); }
    })();
  }, [addToast]);

  return (
    <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr style={{ background: 'var(--bg)' }}>
              <th style={th}>시간</th>
              <th style={th}>주체</th>
              <th style={th}>작업</th>
              <th style={th}>대상</th>
              <th style={th}>메타</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} style={empty}>불러오는 중...</td></tr>}
            {!loading && logs.length === 0 && <tr><td colSpan={5} style={empty}>로그가 없습니다</td></tr>}
            {!loading && logs.map((l) => (
              <tr key={l.id}>
                <td style={td}>{formatDate(l.created_at)}</td>
                <td style={td}>
                  <div style={{ fontSize: 12 }}>{l.actor_type}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{l.actor_name || l.actor_id || '-'}</div>
                </td>
                <td style={td}><code style={{ fontSize: 12 }}>{l.action}</code></td>
                <td style={td}>
                  <div style={{ fontSize: 12 }}>{l.target_type}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{l.target_id}</div>
                </td>
                <td style={td}>
                  <code style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{l.metadata || ''}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------- Shared table styles ----------
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 800 };
const th = { padding: '14px 16px', textAlign: 'left', fontWeight: 600, color: 'var(--text-tertiary)', fontSize: 12, borderBottom: '1px solid var(--divider)' };
const thR = { ...th, textAlign: 'right' };
const td = { padding: '14px 16px', borderBottom: '1px solid var(--divider)' };
const tdR = { ...td, textAlign: 'right', fontWeight: 600 };
const empty = { padding: 30, textAlign: 'center', color: 'var(--text-tertiary)' };
const btnPrimary = {
  padding: '6px 12px', background: 'var(--primary)', color: '#fff', border: 'none',
  borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
};
const btnDanger = {
  padding: '6px 12px', background: '#fff', color: 'var(--danger)',
  border: '1px solid var(--danger)', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
};
const btnGhost = {
  padding: '6px 12px', background: '#fff', color: 'var(--primary)',
  border: '1px solid var(--primary)', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
};

// ---------- Main ----------
const PlatformAdminPage = () => {
  const { toasts, addToast } = useToast();
  const [admin, setAdmin] = useState(() => {
    try {
      const stored = localStorage.getItem('platform_admin');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('stores');

  // Validate token on mount
  useEffect(() => {
    if (!admin) return;
    (async () => {
      try {
        await pfetch('/me');
        const dash = await pfetch('/dashboard');
        setStats(dash.stats || null);
      } catch (e) {
        setAdmin(null);
      }
    })();
  }, [admin?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (a) => {
    setAdmin(a);
    try {
      const dash = await pfetch('/dashboard');
      setStats(dash.stats || null);
    } catch (e) { /* ignore */ }
  };

  const handleLogout = () => {
    localStorage.removeItem('platform_token');
    localStorage.removeItem('platform_admin');
    setAdmin(null);
    setStats(null);
  };

  if (!admin) return <LoginView onLogin={handleLogin} />;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Toasts */}
      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.type}`}>
            <div className="toast-content"><div className="toast-message">{t.msg}</div></div>
          </div>
        ))}
      </div>

      {/* Top bar */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid var(--divider)',
        padding: '14px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'var(--primary-light)', color: 'var(--primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 14,
          }}>PA</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>플랫폼 관리자 콘솔</div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Master Admin</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{admin.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{admin.email}</div>
          </div>
          <button onClick={handleLogout} style={{
            padding: '8px 14px', background: 'transparent', color: 'var(--text-secondary)',
            border: '1px solid var(--divider)', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>로그아웃</button>
        </div>
      </div>

      <div style={{ padding: '28px 24px', maxWidth: 1300, margin: '0 auto' }}>
        {/* Stats */}
        {stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 14,
            marginBottom: 24,
          }}>
            <StatCard label="전체 매장" value={stats.totalStores} />
            <StatCard label="활성 매장 (KYC 승인)" value={stats.activeStores} color="var(--success)" />
            <StatCard label="오늘 매출" value={formatWon(stats.todayRevenue)} color="var(--primary)" />
            <StatCard
              label="대기중 정산"
              value={stats.pendingSettlementsCount}
              subtitle={formatWon(stats.pendingSettlementsAmount)}
              color="var(--warning)"
            />
            <StatCard
              label="누적 수수료 (플랫폼)"
              value={formatWon(stats.totalPlatformFees)}
              subtitle={`PG: ${formatWon(stats.totalPgFees)}`}
            />
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid var(--divider)' }}>
          {[
            { k: 'stores', l: '매장 관리' },
            { k: 'settlements', l: '정산 관리' },
            { k: 'audit', l: '활동 로그' },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              style={{
                padding: '14px 22px',
                background: 'transparent',
                border: 'none',
                borderBottom: `3px solid ${tab === t.k ? 'var(--primary)' : 'transparent'}`,
                color: tab === t.k ? 'var(--primary)' : 'var(--text-secondary)',
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
                marginBottom: -1,
              }}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === 'stores' && <StoresTab addToast={addToast} />}
        {tab === 'settlements' && <SettlementsTab addToast={addToast} />}
        {tab === 'audit' && <AuditTab addToast={addToast} />}
      </div>
    </div>
  );
};

export default PlatformAdminPage;
